import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  dataTestId,
  DISABLING_DISCLAIMER_SCORES,
  ENABLING_DISCLAIMER_SCORES,
} from 'views/Company/CompanyView/components/InterviewGuideSection/InterviewGuideSection.helpers';
import {
  Button,
  EditFormWrapper,
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './InterviewGuideSection.module.scss';

type InterviewGuideSectionProps = {
  company: CompanyPageCompanyFragment;
};

export default function InterviewGuideSection({
  company,
}: InterviewGuideSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <InterviewGuideForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
    />
  ) : (
    <InterviewGuideSectionView
      company={company}
      onEditClick={handleEditClick}
    />
  );
}

type InterviewGuideSectionViewProps = {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
};

function InterviewGuideSectionView({
  company,
  onEditClick,
}: InterviewGuideSectionViewProps) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Interview Guide</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Scores in Interview Guide"
          value={company.isInterviewGuideScoresEnabled ? 'Enabled' : 'Disabled'}
        />
      </div>
    </div>
  );
}

type InterviewGuideFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

type InterviewGuideFormValues = {
  interviewGuideScores: string;
};

type NotificationVisibility = Partial<
  Record<keyof InterviewGuideFormValues, boolean>
>;

function InterviewGuideForm({
  company,
  onCancelClick,
  onSave,
}: InterviewGuideFormProps) {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();
  const [notificationVisibility, setNotificationVisibility] =
    useState<NotificationVisibility>({});

  const handleSave = (values: InterviewGuideFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isInterviewGuideScoresEnabled:
          values.interviewGuideScores === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Interview guide settings updated.',
          dismissable: true,
        });
        setNotificationVisibility({});
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Interview guide settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    interviewGuideScores: company.isInterviewGuideScoresEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
  };

  return (
    <Formik<InterviewGuideFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Interview Guide"
          formId="interview-guide-form"
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id="interview-guide-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="interviewGuideScores"
                name="interviewGuideScores"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label="Scores in Interview Guide"
                data-testid={`${dataTestId}__interview-guide-scores-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={() => {
                  setNotificationVisibility(prev => ({
                    ...prev,
                    interviewGuideScores: true,
                  }));
                }}
              />
              {notificationVisibility.interviewGuideScores && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  {values.interviewGuideScores
                    ? ENABLING_DISCLAIMER_SCORES
                    : DISABLING_DISCLAIMER_SCORES}
                </InlineNotification>
              )}
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
